import { fetchLiquidations } from "@/apis/lendingRequests"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { networkAtom } from "@/atoms/settings"
import { SelectedReserveType } from "@/atoms/shared"
import useActionOnChain from "@/hooks/useActionOnchain"
import useCheckingOnChain from "@/hooks/useCheckingOnChain"
import useTrans from "@/hooks/useTrans"
import { useTransactionHandler } from "@/hooks/useTransactionHandler"
import useWalletConnect from "@/hooks/useWalletConnect"
import { RENEC_PADDING_FOR_RENT_AND_FEE } from "@/utils/actions/commons"
import { DEFAULT_DECIMALS, RENEC } from "@/utils/constants"
import { TransactionDetail } from "@/utils/dataTypes"
import { replaceLocaleStringWithValues } from "@/utils/helpers"
import { Typography } from "@mui/material"
import { BlockchainNetwork, ConstantsHelper } from "@renec-foundation/relend-adapter"
import { PublicKey } from "@solana/web3.js"
import BigNumber from "bignumber.js"
import { useAtomValue } from "jotai"
import React, { useCallback, useRef, useState } from "react"
import { useInfiniteQuery } from "react-query"
import { LiquidationResponse } from "./types"
import View from "./view"

const DEFAULT_LIQUIDATION_RESPONSE: LiquidationResponse = {
  page: 0,
  per_page: 10,
  next: null,
  total_count: 0,
  liquidations: [],
}

const PER_PAGE = 10

const Container = () => {
  const trans = useTrans()
  const { address, connected, getBalanceNativeToken } = useWalletConnect()
  const { liquidateObligation } = useActionOnChain()
  const { handleLiquidateResult } = useTransactionHandler()
  const { getTokenAccountInfo } = useCheckingOnChain()
  const walletConnectButtonRef = useRef<HTMLButtonElement | null>(null)
  const network = useAtomValue(networkAtom)

  const [perPage] = useState<number>(PER_PAGE)
  const [loading, setLoading] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [transactionDetail, setTransactionDetail] = useState<TransactionDetail>({
    isActive: false,
    title: "",
    content: "",
    ctaButtonLink: "",
  })
  const selectedObligation = useAtomValue(selectedMainObligationAtom)
  const {
    data: liquidations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    ["fetchLiquidations"],
    async ({ pageParam }) => {
      const res = await fetchLiquidations(pageParam, perPage)

      if (res && !res.error) {
        return res as LiquidationResponse
      } else {
        return DEFAULT_LIQUIDATION_RESPONSE
      }
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.next
      },
      refetchInterval: 1500,
    },
  )

  const handleConnectWalletClick = useCallback(() => {
    if (walletConnectButtonRef.current) {
      const demonWalletConnectButton = walletConnectButtonRef.current.querySelector("button")
      if (demonWalletConnectButton) {
        demonWalletConnectButton.click()
      }
    }
  }, [])

  const handleLiquidateObligation = async (
    obligation: PublicKey,
    needToPayAmount: BigNumber,
    depositReserve: PublicKey,
    borrowReserve: PublicKey,
    selectedBorrowReserve?: SelectedReserveType,
    selectedDepositReserve?: SelectedReserveType,
  ) => {
    if (!connected) {
      handleConnectWalletClick()
      return
    }

    if (!loading && selectedBorrowReserve && address) {
      setLoading(true)
      const selectedBorrowReserveBalance =
        selectedBorrowReserve.symbol === RENEC
          ? (await getBalanceNativeToken()) - RENEC_PADDING_FOR_RENT_AND_FEE
          : (await getTokenAccountInfo(new PublicKey(address), new PublicKey(selectedBorrowReserve.mintAddress)))
              .tokenBalance
      const ctaButtonLink = ConstantsHelper.isRenecChain(network)
        ? "https://nemo.renec.org/swap/reUSD_RENEC"
        : "https://jup.ag/swap/USDC-SOL"
      const submitAmount = new BigNumber(BigNumber.min(needToPayAmount, selectedBorrowReserveBalance ?? 0))
      if (!submitAmount || submitAmount.isZero()) {
        setTransactionDetail({
          isActive: true,
          ctaButtonLink: ctaButtonLink,
          title: trans.dashboard.modal.errors.failed_to_complete,
          ctaButtonTitle: replaceLocaleStringWithValues(
            trans.dashboard.modal.errors.deposit_more_token,
            selectedBorrowReserve.symbol,
          ),
          isDisplayingGoHomeButton: false,
          content: (
            <Typography color='red' textAlign='center'>
              {trans.dashboard.modal.errors.insufficient_balance(selectedBorrowReserve.name)}
            </Typography>
          ),
        })
        setIsModalOpen(true)
        setLoading(false)
        return
      }

      const roundedAmount = submitAmount.shiftedBy(selectedBorrowReserve?.decimals ?? DEFAULT_DECIMALS).toFixed(0)

      const result = await liquidateObligation(
        BlockchainNetwork.SOLANA,
        obligation,
        roundedAmount,
        depositReserve,
        borrowReserve,
        trans,
      )
      handleLiquidateResult({
        result,
        trans,
        amount: needToPayAmount.toNumber(),
        walletAddress: address,
        selectedBorrowReserve,
        selectedDepositReserve,
        obligationAddress: selectedObligation?.address,
        obligationLiquidated: obligation.toBase58(),
        setTransactionDetail,
      })
      setIsModalOpen(true)
      setLoading(false)
    }
  }

  const handleCloseModal = useCallback(() => {
    setTransactionDetail({
      isActive: false,
      ctaButtonLink: "",
      title: "",
      content: "",
    })
    setIsModalOpen(false)
  }, [])

  const doesHaveItems = (liquidations && liquidations.pages[0].liquidations.length > 0) || false

  return (
    <View
      doesHaveItems={doesHaveItems}
      handleCloseModal={handleCloseModal}
      walletConnectButtonRef={walletConnectButtonRef}
      isModalOpen={isModalOpen}
      transactionDetail={transactionDetail}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isLoading}
      loading={loading}
      handleLiquidateObligation={handleLiquidateObligation}
      liquidations={liquidations}
    />
  )
}

export default Container
